import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { EmployeeSidebar } from "./EmployeeDetails";
import { SideBarContentHeader } from "@/common/ui/SideBar";
import { VerticalCenter } from "@/common/css";
import { AddEmployeeConfig } from "@/utils/constant/formConfig.constant";
import { getNestedValue } from "@/utils/helper/form";
import { ViewField } from "@/components/dashboard/setting/profile/ProfileView";
import { Image } from "@/common/ui/Image";
import { DefaultText, H3 } from "@/common/ui/Text";
import { useParams } from "react-router-dom";
import { useEmployeeInfo } from "@/services/employee.services";
import { EmployeeDetailsSubSection } from "@/common/ui/Loader";
import { pageEventSelector } from "@/store/features/dashboardSlice";
import { useSelector } from "react-redux";
import CustomLink from "@/common/ui/CustomLink";

interface EmployeeInfoProps {
  tab: EmployeeSidebar;
}

const EmployeeInfo: React.FC<EmployeeInfoProps> = ({ tab }) => {
  const [openSections, setOpenSections] = useState<string[]>(["Basic information"]);
  const pageEvent = useSelector(pageEventSelector);

  const { employeeId } = useParams();
  const { isLoading, data, refetch } = useEmployeeInfo({ empId: employeeId, view_typ: tab.apiKey });
  const isAccountOwnerRole = (data as any)?.userTyp?.label === "Account Owner";

  React.useEffect(() => {
    if (pageEvent?.keys.includes(tab?.name) && pageEvent?.type === "refresh") {
      refetch();
    }
  }, [pageEvent]);

  const getSections = (data: any) => {
    const baseSections = ["Basic information"];
    if (data?.social) {
      baseSections.push("Social information");
    }
    return baseSections;
  };

  if (isLoading) return <EmployeeDetailsSubSection />;

  const sections = getSections(data);
  const socialCount = data?.social ? Object.entries(data.social).filter(([_, value]) => value).length : 0;

  return (
    <div className="flex flex-col w-full h-full">
      {tab?.name && <SideBarContentHeader heading={tab.name} className="!p-4 !z-10 bg-white rounded-llg rounded-b-none" />}
      <div className="overflow-y-auto h-full">
        <div className="flex flex-col gap-5 pb-4">
          {sections.map((section) => {
            const isOpen = openSections.includes(section);
            if (section === "Social information" && data?.social) {
              return (
                <VerticalCenter key={section}>
                  <div
                    className="flex items-center bg-primaryOpacity-5 w-full pl-5 py-15 gap-x-2 hover:cursor-pointer"
                    onClick={() => setOpenSections((prev) => (prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]))}
                  >
                    <ChevronDown className={`cursor-pointer text-text-primary bg-transparent z-0 ${isOpen ? "rotate-180" : ""}`} height={20} width={20} />
                    <H3 text={`Social information (${socialCount})`} className="select-none" />
                  </div>
                  {isOpen && (
                    <div className="grid grid-cols-2  xl:grid-cols-3 gap-2 w-full">
                      {Object.entries(data.social).map(([key, value]) => {
                        if (!value) return null;
                        return (
                          <div className="px-3 py-2 overflow-hidden flex flex-col gap-2" key={key}>
                            <DefaultText text={key} className="capitalize" />
                            <CustomLink
                              key={key}
                              link={{ text: value as string, value: value as string }}
                              isExternal
                              className="text-13 !p-0 text-primary underline break-all truncate line-clamp-2 max-w-[200px] xl:max-w-[250px]"
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </VerticalCenter>
              );
            }

            // Default section rendering
            return (
              <VerticalCenter key={section}>
                <div
                  className="flex items-center bg-primaryOpacity-5 w-full pl-5 py-15 gap-x-2 hover:cursor-pointer"
                  onClick={() => setOpenSections((prev) => (prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]))}
                >
                  <ChevronDown className={`cursor-pointer text-text-primary bg-transparent z-0 ${isOpen ? "rotate-180" : ""}`} height={20} width={20} />
                  <H3
                    text={`${section} (${
                      AddEmployeeConfig.sections[0].widgets.filter(
                        (widget: any) => !widget?.hideOnViewMode && !(widget.field === "reportingManager" && isAccountOwnerRole)
                      ).length
                    })`}
                    className="select-none"
                  />
                </div>
                {isOpen && (
                  <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 w-full">
                    {AddEmployeeConfig.sections.map((section: any) =>
                      section.widgets.map((widget: any) => {
                        if (widget?.hideOnViewMode) return null;
                        if (widget.field === "reportingManager" && isAccountOwnerRole) return null;
                        const prefillField = widget?.viewField || widget?.apiField || widget.field;
                        const fieldValue = getNestedValue(data, prefillField);
                        return (
                          <div className="px-3 py-2 overflow-hidden" key={widget.field}>
                            <ViewField
                              label={widget.label}
                              viewType={widget?.viewType}
                              fieldType={widget.widgetType}
                              value={fieldValue || "-"}
                              field={widget.field}
                              C={widget.field === "address.lineAddress" ? <Image alt=" " src="/icons/direction.svg" /> : ""}
                            />
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </VerticalCenter>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default EmployeeInfo;
